import React, { useState } from 'react';
import { FiGift } from 'react-icons/fi';
// import { FiInfo } from 'react-icons/fi'; // only used by the commented-out Type callout below

import { Modal } from '@components/Modal';
import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { Button, buttonVariants } from '@components/Button';
import { Input } from '@components/Input';
import { TextArea } from '@components/TextArea';
// import { Dropdown, filterBarSelectStyles } from '@components/Dropdown'; // only used by the commented-out Type field below
// import { Callout, calloutVariants } from '@components/Callout'; // only used by the commented-out Type callout below
import { SingleDatePicker } from '@components/SingleDatePicker';
import { SettingsPanelHeader } from './SettingsPanelHeader.component';

import {
  holidayTypes,
  // HOLIDAY_TYPE_OPTIONS, // only used by the commented-out Type field below
  HOLIDAY_DESCRIPTION_MAX_LENGTH,
  getHolidayType,
} from '@/constants/holidays';

import styles from './HolidayFormModal.module.scss';

const emptyFormData = () => ({
  name: '',
  date: null,
  type: holidayTypes.public,
  description: '',
});

// const typeOptionByValue = (value) =>
//   HOLIDAY_TYPE_OPTIONS.find((option) => option.value === value) || null; // only used by the commented-out Type field below

export const HolidayFormModal = ({
  isVisible,
  onClose,
  onSubmit,
  initialHoliday,
  existingDates = [],
  isSaving = false,
}) => {
  const isEditMode = !!initialHoliday;
  const [formData, setFormData] = useState(emptyFormData());

  // Reset the form the moment isVisible turns true, in the same render -
  // not via an Effect, which would run one render late and briefly show the
  // *previous* open's leftover name/date. If that previous holiday had just
  // been added, its date is now in existingDates, so that stale leftover
  // date would flag as a duplicate of itself for one frame before an Effect
  // could catch up. See https://react.dev/learn/you-might-not-need-an-effect
  // ("Resetting all state when a prop changes").
  const [prevIsVisible, setPrevIsVisible] = useState(isVisible);
  if (isVisible !== prevIsVisible) {
    setPrevIsVisible(isVisible);
    if (isVisible) {
      setFormData(
        initialHoliday
          ? {
              name: initialHoliday.name,
              date: initialHoliday.date,
              type: getHolidayType(initialHoliday),
              description: initialHoliday.description || '',
            }
          : emptyFormData()
      );
    }
  }

  // Gated on isVisible and !isSaving - both close windows where this would
  // otherwise flag a false duplicate against the record being submitted:
  // - isVisible: Modal fades out over its close transition rather than
  //   unmounting instantly, so this still renders for a moment after a
  //   successful save, by which point existingDates already includes it.
  // - !isSaving: a local write's onSnapshot echo can reach existingDates
  //   before the save's own await resolves, so the record can appear in
  //   the list while the request is still in flight and the form (with
  //   that same date) is still open. The check is moot here regardless -
  //   fields are disabled while saving, so nothing the user could act on
  //   has changed.
  const isDuplicateDate =
    isVisible &&
    !isSaving &&
    !!formData.date &&
    existingDates.some(
      (date) => date === formData.date && date !== initialHoliday?.date
    );

  const canSubmit = !!formData.date && !!formData.name.trim();

  const handleSubmit = () => {
    if (!canSubmit || isDuplicateDate || isSaving) return;

    onSubmit({
      date: formData.date,
      name: formData.name.trim(),
      optional: formData.type === holidayTypes.optional,
      description: formData.description.trim(),
    });
  };

  return (
    <Modal isVisible={isVisible} onClose={onClose} preventClose={isSaving}>
      <div className={styles.formModal}>
        <SettingsPanelHeader
          icon={<FiGift size={20} color="var(--indigo-600)" />}
          title={isEditMode ? 'Edit Holiday' : 'Add Holiday'}
          description={
            isEditMode
              ? 'Update this holiday for your company.'
              : 'Create a new holiday for your company.'
          }
        />

        <Input
          id="holidayFormName"
          label="Holiday Name *"
          size="lg"
          placeholder="Enter holiday name"
          value={formData.name}
          disabled={isSaving}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, name: e.target.value }))
          }
        />

        <div className={styles.dateTypeRow}>
          <div className={styles.field}>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              className={styles.fieldLabel}
            >
              Date *
            </Text>
            <SingleDatePicker
              date={formData.date}
              onDateChange={(value) =>
                setFormData((prev) => ({ ...prev, date: value }))
              }
              compact
              disableFutureDates={false}
              disabled={isSaving}
            />
            {isDuplicateDate && (
              <Text
                variant={textVariantsEnum.Text_xs}
                fontWeight={fontWeightEnum.Normal}
                color="error-500"
              >
                A holiday already exists on this date
              </Text>
            )}
          </div>
          {/* Type (Public/Optional) is not fully wired - optional holidays
              aren't actually enforced or selectable anywhere for employees,
              so the picker is hidden and every holiday is treated as public. */}
          {/* <div className={styles.field}>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              className={styles.fieldLabel}
            >
              Type *
            </Text>
            <Dropdown
              options={HOLIDAY_TYPE_OPTIONS}
              value={typeOptionByValue(formData.type)}
              onChange={(option) =>
                setFormData((prev) => ({ ...prev, type: option.value }))
              }
              selectStyles={filterBarSelectStyles}
            />
          </div> */}
        </div>

        {/* <Callout variant={calloutVariants.info} icon={<FiInfo size={14} />}>
          Public holidays are mandatory for all employees. Optional holidays can
          be chosen by employees.
        </Callout> */}

        <div className={styles.descriptionField}>
          <TextArea
            id="holidayFormDescription"
            label="Description (Optional)"
            placeholder="Add a note about this holiday"
            rows={3}
            maxLength={HOLIDAY_DESCRIPTION_MAX_LENGTH}
            value={formData.description}
            disabled={isSaving}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, description: e.target.value }))
            }
          />
          <Text
            variant={textVariantsEnum.Text_xs}
            fontWeight={fontWeightEnum.Normal}
            color="gray-400"
            className={styles.charCount}
          >
            {formData.description.length}/{HOLIDAY_DESCRIPTION_MAX_LENGTH}
          </Text>
        </div>

        <div className={styles.footerRow}>
          <Button
            text="Cancel"
            variant={buttonVariants.outline}
            onClick={onClose}
            disabled={isSaving}
            style={{ flex: 1 }}
          />
          <Button
            text={isEditMode ? 'Save Changes' : 'Add Holiday'}
            variant={buttonVariants.primary}
            onClick={handleSubmit}
            disabled={!canSubmit || isDuplicateDate || isSaving}
            loading={isSaving}
            style={{ flex: 1 }}
          />
        </div>
      </div>
    </Modal>
  );
};

HolidayFormModal.displayName = 'HolidayFormModal';
